import { useConnection } from 'hooks/useConnection'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import ms from 'ms.macro'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SerializableTransactionReceipt } from 'state/transactions/types'
import { toSerializableReceipt } from 'state/transactions/utils'
import type { Hex } from 'viem'

import { useLatestBlock } from '../../../hooks/useLatestBlock'

interface Transaction {
  addedTime: number
  receipt?: unknown
  lastCheckedBlockNumber?: number
  from: string
}

export function shouldCheck(lastBlockNumber: number, tx: Transaction): boolean {
  if (tx.receipt) return false
  if (!tx.lastCheckedBlockNumber) return true
  const blocksSinceCheck = lastBlockNumber - tx.lastCheckedBlockNumber
  if (blocksSinceCheck < 1) return false
  const minutesPending = (new Date().getTime() - tx.addedTime) / ms`1m`
  if (minutesPending > 60) {
    // every 10 blocks if pending longer than an hour
    return blocksSinceCheck > 9
  } else if (minutesPending > 5) {
    // every 3 blocks if pending longer than 5 minutes
    return blocksSinceCheck > 2
  } else {
    // otherwise every block
    return true
  }
}

interface UpdaterProps {
  pendingTransactions: { [hash: string]: Transaction }
  onCheck: (tx: { chainId: number; hash: string; blockNumber: number }) => void
  onReceipt: (tx: { chainId: number; hash: string; receipt: SerializableTransactionReceipt }) => void
}

/**
 * Component that waits for a single transaction receipt using multisig-aware utility
 */
function TransactionReceiptUpdater({
  hash,
  chainId,
  transaction,
  lastBlockNumber,
  onCheck,
  onReceipt,
}: {
  hash: string
  chainId: number
  transaction: Transaction
  lastBlockNumber: number | undefined
  onCheck: (tx: { chainId: number; hash: string; blockNumber: number }) => void
  onReceipt: (tx: { chainId: number; hash: string; receipt: SerializableTransactionReceipt }) => void
}) {
  const { wagmiPublicClient, account } = useConnection()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const [isPending, setIsPending] = useState(false)
  const hasProcessedRef = useRef(false)
  const isWaitingRef = useRef(false)

  const enabled = useMemo(() => {
    if (!lastBlockNumber) return false
    return shouldCheck(lastBlockNumber, transaction)
  }, [lastBlockNumber, transaction])

  useEffect(() => {
    if (!enabled || !wagmiPublicClient || hasProcessedRef.current || isWaitingRef.current) {
      return
    }

    isWaitingRef.current = true
    setIsPending(true)

    waitForTransactionReceipt({
      hash: hash as Hex,
      address: (transaction.from as Hex) ?? account,
    })
      .then((receipt) => {
        if (!hasProcessedRef.current) {
          hasProcessedRef.current = true
          const serializableReceipt = toSerializableReceipt(receipt)
          onReceipt({ chainId, hash, receipt: serializableReceipt })
          setIsPending(false)
        }
      })
      .catch((error) => {
        console.error('Error fetching transaction receipt:', error)
        setIsPending(false)
        if (lastBlockNumber) {
          onCheck({ chainId, hash, blockNumber: lastBlockNumber })
        }
      })
      .finally(() => {
        isWaitingRef.current = false
      })
  }, [
    enabled,
    wagmiPublicClient,
    account,
    waitForTransactionReceipt,
    transaction.from,
    hash,
    chainId,
    lastBlockNumber,
    onCheck,
    onReceipt,
  ])

  // Update lastCheckedBlockNumber when enabled and still pending (to track polling progress)
  // Only update when block number changes to avoid excessive calls
  const prevBlockNumberRef = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (
      enabled &&
      isPending &&
      lastBlockNumber &&
      !transaction.receipt &&
      prevBlockNumberRef.current !== lastBlockNumber
    ) {
      // Update the last checked block number to track that we're actively checking
      prevBlockNumberRef.current = lastBlockNumber
      onCheck({ chainId, hash, blockNumber: lastBlockNumber })
    }
  }, [enabled, isPending, lastBlockNumber, transaction.receipt, chainId, hash, onCheck])

  return null
}

export default function Updater({ pendingTransactions, onCheck, onReceipt }: UpdaterProps): JSX.Element | null {
  const { chainId } = useConnection()

  const { blockNumber } = useLatestBlock()

  if (!chainId || !blockNumber) return null

  // Convert bigint to number for compatibility
  const lastBlockNumberNum = Number(blockNumber)

  return (
    <>
      {Object.keys(pendingTransactions).map((hash) => (
        <TransactionReceiptUpdater
          key={hash}
          hash={hash}
          chainId={chainId}
          transaction={pendingTransactions[hash]}
          lastBlockNumber={lastBlockNumberNum}
          onCheck={onCheck}
          onReceipt={onReceipt}
        />
      ))}
    </>
  )
}
